import React, { useEffect, useState } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import { LayoutDashboard, Package, Warehouse, Factory, Tags, Leaf, Building2, Printer, ClipboardCheck, Scan, ShoppingCart, FileText, ClipboardList, FileSignature, FileMinus, LogOut, FlaskConical, ScanLine, FileImage, Briefcase, Activity, Boxes, PartyPopper, ChefHat, Users, Mail, UserCog, Truck, Trash2, BarChart3, BookUser, Globe, ExternalLink, AlertTriangle, PackagePlus, Calculator, Utensils, Wrench, Users2 } from "lucide-react";
import { useAuth } from "../lib/auth";
import { canAccessPath, roleLabel } from "../lib/permissions";
import { api } from "../lib/api";
import { InstallPWAButton } from "./InstallPWA";

const navItems = [
  { to: "/", label: "Tableau de bord", icon: LayoutDashboard, testid: "nav-dashboard", end: true, hideForRoles: ["field_operator"] },
  { to: "/statistiques", label: "Statistiques", icon: BarChart3, testid: "nav-statistics", hideForRoles: ["field_operator"] },
  { to: "/tournee", label: "Ma tournée", icon: LayoutDashboard, testid: "nav-tournee", end: true, onlyForRoles: ["field_operator"] },
  { to: "/produits", label: "Produits", icon: Package, testid: "nav-products" },
  { to: "/stocks", label: "Stocks & Lots", icon: Warehouse, testid: "nav-stocks" },
  { to: "/ordres", label: "Ordres de Fabrication", icon: Factory, testid: "nav-orders" },
  { to: "/commandes-clients", label: "Commandes clients", icon: ClipboardList, testid: "nav-client-orders" },
  { to: "/ventes", label: "Ventes", icon: ShoppingCart, testid: "nav-sales" },
  { to: "/bons-livraison", label: "Bons de livraison", icon: FileText, testid: "nav-delivery-notes" },
  { to: "/factures", label: "Factures", icon: FileSignature, testid: "nav-invoices" },
  { to: "/notes-de-credit", label: "Notes de crédit", icon: FileMinus, testid: "nav-credit-notes" },
  { to: "/reconciliation", label: "Réconciliation", icon: Wrench, testid: "nav-reconciliation", hideForRoles: ["field_operator"] },
  { to: "/devis", label: "Devis traiteur", icon: PartyPopper, testid: "nav-catering-quotes" },
  { to: "/courriers", label: "Courriers", icon: Mail, testid: "nav-company-letters" },
  { to: "/distributeurs", label: "Distributeurs", icon: Building2, testid: "nav-distributors" },
  { to: "/nayax/cockpit", label: "Cockpit Nayax", icon: Activity, testid: "nav-nayax-cockpit", hideForRoles: ["field_operator"] },
  { to: "/tournees-reappro", label: "Tournées réappro", icon: Truck, testid: "nav-refill-tours" },
  { to: "/pertes-dlc", label: "Pertes DLC", icon: Trash2, testid: "nav-dlc-losses" },
  { to: "/rapports/retirees", label: "Rapport Pertes / Retraits", icon: BarChart3, testid: "nav-reports-retirees", hideForRoles: ["field_operator"] },
  { to: "/etat-distributeurs", label: "État distributeurs", icon: Activity, testid: "nav-distributors-overview" },
  { to: "/contacts", label: "Carnet d'adresses", icon: BookUser, testid: "nav-contacts", hideForRoles: ["field_operator"] },
  { to: "/parties", label: "Carnet central (V1)", icon: Users2, testid: "nav-parties", hideForRoles: ["field_operator"] },
  { to: "/revendeurs", label: "Revendeurs", icon: Briefcase, testid: "nav-resellers" },
  { to: "/candidatures-pro", label: "Candidatures pro", icon: BookUser, testid: "nav-candidatures-pro" },
  { to: "/partenaires", label: "Partenaires", icon: UserCog, testid: "nav-partners" },
  { to: "/partenaires/retribution", label: "Rétribution partenaires", icon: Calculator, testid: "nav-partner-commission", hideForRoles: ["field_operator"] },
  { to: "/parametres/clients-boutique", label: "Clients B2C", icon: Users, testid: "nav-shop-customers" },
  { to: "/packs", label: "Packs B2B", icon: Boxes, testid: "nav-packs" },
  { to: "/inventaires", label: "Inventaires", icon: ClipboardCheck, testid: "nav-inventories" },
  { to: "/tests-labo", label: "Tests laboratoire", icon: FlaskConical, testid: "nav-lab-tests" },
  { to: "/tracabilite", label: "Traçabilité", icon: Scan, testid: "nav-traceability" },
  { to: "/haccp/registre", label: "Registre HACCP", icon: ClipboardList, testid: "nav-haccp-register", hideForRoles: ["field_operator"] },
  { to: "/scan", label: "Scan terrain", icon: ScanLine, testid: "nav-scan-appro" },
  { to: "/etiquettes", label: "Étiquettes", icon: Tags, testid: "nav-labels" },
  { to: "/cartes-imprimables", label: "Cartes imprimables", icon: FileImage, testid: "nav-menu-cards" },
  { to: "/carte-traiteur", label: "Carte traiteur", icon: Utensils, testid: "nav-catering-menu" },
  { to: "/guide", label: "Guide opérateur", icon: ChefHat, testid: "nav-guide" },
  { to: "/parametres", label: "Paramètres", icon: Printer, testid: "nav-settings" },
];

export default function Layout() {
  const location = useLocation();
  const { user, logout } = useAuth();
  const role = user && user !== false ? user.role : null;
  const [momaBadge, setMomaBadge] = useState(0);
  // Phase 101cg (18/02/2026) — Badge notification candidatures pro en attente
  // (inscriptions revendeur via boutique OU landing). Cas d'usage : l'admin ne
  // voyait plus qu'elle avait des candidatures à traiter (dispersion B2C ↔ Revendeurs).
  const [resellerAppsBadge, setResellerAppsBadge] = useState(0);
  // Phase 101ew — Badge candidatures hôte distributeur en attente
  const [hostAppsBadge, setHostAppsBadge] = useState(0);
  const [webOrdersBadge, setWebOrdersBadge] = useState(0);
  // Phase 101dg (Feb 2026) — Bandeau rouge global tant que le mode expert Nayax
  // est ACTIF (autorise les écritures Nayax). Poll toutes les 60s + rafraîchi
  // instantanément par event `nayax-expert-mode-changed` (dispatché depuis
  // Settings.jsx à la bascule).
  const [expertMode, setExpertMode] = useState(false);

  // Phase 101bl — Badge notification refills MOMA en attente
  // Phase 101gd (Feb 2026) — Fix perf : `location.pathname` retiré des deps.
  // Avant : les 3 badges refetch à CHAQUE changement de route → 6+ appels
  // parasites par navigation (constaté sur /ordres/planning). Maintenant :
  // 1er fetch au montage + poll 60s. Suffit largement pour un badge.
  useEffect(() => {
    if (!user || user === false || role === "field_operator") return;
    let cancelled = false;
    const fetchBadges = async () => {
      try {
        const { data } = await api.get("/moma-refills/pending/count");
        if (!cancelled) setMomaBadge(data.count || 0);
      } catch (_) { /* ignore */ }
      try {
        const { data } = await api.get("/admin/reseller-applications", { params: { status: "pending", limit: 1 } });
        if (!cancelled) setResellerAppsBadge((data?.stats?.pending) || 0);
      } catch (_) { /* ignore */ }
      try {
        const { data } = await api.get("/admin/distributor-host-applications", { params: { status: "pending", limit: 1 } });
        if (!cancelled) setHostAppsBadge((data?.stats?.pending) || 0);
      } catch (_) { /* ignore */ }
      try {
        const { data } = await api.get("/shop-admin/web-orders/pending-count");
        if (!cancelled) setWebOrdersBadge(data?.count || 0);
      } catch (_) { /* ignore */ }
    };
    fetchBadges();
    const t = setInterval(fetchBadges, 60000); // toutes les 60s
    return () => { cancelled = true; clearInterval(t); };
  }, [user, role]);

  // Phase 101dg — Poll état mode expert Nayax + refresh instantané via event
  useEffect(() => {
    if (!user || user === false || role === "field_operator") return;
    let cancelled = false;
    const fetchExpertMode = async () => {
      try {
        const { data } = await api.get("/settings");
        if (!cancelled) setExpertMode(!!data?.nayax_expert_push_mode);
      } catch (_) { /* ignore */ }
    };
    fetchExpertMode();
    const t = setInterval(fetchExpertMode, 60000);
    const onEvent = (e) => setExpertMode(!!e?.detail?.enabled);
    window.addEventListener("nayax-expert-mode-changed", onEvent);
    return () => {
      cancelled = true;
      clearInterval(t);
      window.removeEventListener("nayax-expert-mode-changed", onEvent);
    };
  }, [user, role]);

  const visibleNav = navItems.filter((n) => {
    if (n.onlyForRoles && !n.onlyForRoles.includes(role)) return false;
    if (n.hideForRoles && n.hideForRoles.includes(role)) return false;
    return canAccessPath(role, n.to);
  });
  const current = visibleNav.find((n) => (n.end ? location.pathname === n.to : location.pathname.startsWith(n.to))) || visibleNav[0] || navItems[0];

  return (
    <div className="flex min-h-screen bg-page">
      {/* Sidebar */}
      <aside className="w-[260px] shrink-0 bg-surface-alt border-r border-[#E5E5E0] no-print flex flex-col">
        <div className="px-6 py-6 border-b border-[#E5E5E0]">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-md bg-[#1F4E3D] flex items-center justify-center">
              <Leaf className="w-5 h-5 text-white" strokeWidth={2.2} />
            </div>
            <div>
              <div className="font-heading text-lg font-semibold text-[#1A1C1A] leading-none">AgroManage</div>
              <div className="text-xs text-[#575A57] mt-1">Production & Traçabilité</div>
            </div>
          </div>
        </div>
        <nav className="flex-1 px-3 py-4 space-y-1">
          {visibleNav.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              data-testid={item.testid}
              className={({ isActive }) => `sidebar-link ${isActive ? "active" : ""}`}
            >
              <item.icon className="w-4 h-4" />
              <span className="flex-1">{item.label}</span>
              {item.to === "/nayax/cockpit" && momaBadge > 0 && (
                <span
                  className="ml-auto text-[10px] bg-[#E8B94B] text-white px-1.5 py-0.5 rounded-full font-bold min-w-[20px] text-center"
                  data-testid="nav-moma-badge"
                  title={`${momaBadge} refill(s) MOMA à saisir`}
                >
                  {momaBadge}
                </span>
              )}
              {item.to === "/revendeurs" && resellerAppsBadge > 0 && (
                <span
                  className="ml-auto text-[10px] bg-[#C05C47] text-white px-1.5 py-0.5 rounded-full font-bold min-w-[20px] text-center"
                  data-testid="nav-reseller-apps-badge"
                  title={`${resellerAppsBadge} candidature(s) revendeur en attente`}
                >
                  {resellerAppsBadge}
                </span>
              )}
              {item.to === "/candidatures-pro" && (resellerAppsBadge + hostAppsBadge) > 0 && (
                <span
                  className="ml-auto text-[10px] bg-[#8C6B2F] text-white px-1.5 py-0.5 rounded-full font-bold min-w-[20px] text-center"
                  data-testid="nav-candidatures-pro-badge"
                  title={`${resellerAppsBadge} revendeur(s) + ${hostAppsBadge} hôte(s) en attente`}
                >
                  {resellerAppsBadge + hostAppsBadge}
                </span>
              )}
              {item.to === "/commandes-clients" && webOrdersBadge > 0 && (
                <span
                  className="ml-auto text-[10px] bg-[#C05C47] text-white px-1.5 py-0.5 rounded-full font-bold min-w-[20px] text-center"
                  data-testid="nav-web-orders-badge"
                  title={`${webOrdersBadge} commande(s) web à traiter (TWINT à vérifier ou payées)`}
                >
                  {webOrdersBadge}
                </span>
              )}
            </NavLink>
          ))}
          {/* Phase 101cv (Feb 2026) — Lien direct vers la boutique publique.
              Ouvre dans un nouvel onglet pour préserver la session admin. */}
          <a
            href="/boutique"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 px-2.5 py-2 mt-2 text-xs text-[#575A57] hover:text-[#1F4E3D] hover:bg-white rounded-md transition-colors border-t border-[#E5E5E0] pt-3"
            data-testid="nav-public-site"
            title="Ouvrir la boutique publique dans un nouvel onglet"
          >
            <Globe className="w-4 h-4" />
            <span className="flex-1">Voir le site public</span>
            <ExternalLink className="w-3 h-3 opacity-60" />
          </a>
        </nav>
        <div className="px-6 py-4 border-t border-[#E5E5E0] space-y-3">
          {user && user !== false && (
            <button
              onClick={logout}
              className="w-full flex items-center gap-2 px-2.5 py-2 text-xs text-[#575A57] hover:text-[#1A1C1A] hover:bg-white rounded-md transition-colors group"
              data-testid="logout-btn"
              title={`Déconnexion (${user.email})`}
            >
              <LogOut className="w-3.5 h-3.5" />
              <span className="truncate flex-1 text-left">
                <span className="block font-medium text-[#1A1C1A] text-[11px] truncate">{user.email}</span>
                <span className="block text-[10px] uppercase tracking-wider">
                  {roleLabel(user.role)} · Déconnexion
                </span>
              </span>
            </button>
          )}
          <div className="text-xs text-[#575A57]">
            <div className="uppercase tracking-[0.2em] mb-1 font-semibold">Version</div>
            <div className="font-mono">v1.0 — Agroalimentaire</div>
          </div>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 min-w-0">
        {/* Phase 101dg — Bannière rouge globale quand mode expert Nayax ACTIF.
            Visible sur toutes les pages tant que le flag est ON. */}
        {expertMode && (
          <div
            className="bg-[#C05C47] text-white px-6 py-2.5 flex items-center justify-between gap-4 no-print border-b border-[#A04A38]"
            data-testid="nayax-expert-mode-banner"
          >
            <div className="flex items-center gap-3 min-w-0">
              <AlertTriangle className="w-5 h-5 shrink-0" strokeWidth={2.5} />
              <div className="min-w-0">
                <div className="text-sm font-semibold leading-tight">
                  Mode expert Nayax ACTIF · écritures autorisées
                </div>
                <div className="text-[11px] opacity-90 leading-tight mt-0.5">
                  Les actions de synchronisation avancée peuvent modifier la configuration
                  des distributeurs sur Nayax. Désactivez dès que possible.
                </div>
              </div>
            </div>
            <NavLink
              to="/parametres"
              className="text-xs font-medium uppercase tracking-wider bg-white/15 hover:bg-white/25 px-3 py-1.5 rounded shrink-0 whitespace-nowrap"
              data-testid="nayax-expert-mode-banner-btn"
            >
              Désactiver
            </NavLink>
          </div>
        )}
        {/* Phase 101dj (Feb 2026) — Bannière ambre : refills MOMA à valider.
            Rappel visuel post-refill via MOMA portal. L'utilisatrice a besoin
            de la validation manuelle (choix du lot par distributeur, en fonction
            de la proximité / du succès des plats). Le bandeau redirige vers le
            Cockpit Nayax où se fait la validation lot par lot. */}
        {!expertMode && momaBadge > 0 && (
          <div
            className="bg-[#E8B94B] text-[#3E2C0C] px-6 py-2.5 flex items-center justify-between gap-4 no-print border-b border-[#C99931]"
            data-testid="moma-refills-pending-banner"
          >
            <div className="flex items-center gap-3 min-w-0">
              <PackagePlus className="w-5 h-5 shrink-0" strokeWidth={2.5} />
              <div className="min-w-0">
                <div className="text-sm font-semibold leading-tight">
                  {momaBadge} refill{momaBadge > 1 ? "s" : ""} MOMA à valider
                </div>
                <div className="text-[11px] opacity-80 leading-tight mt-0.5">
                  AM a détecté un réapprovisionnement fait via MOMA — validez le
                  lot Conthey associé pour actualiser le stock (traçabilité HACCP).
                </div>
              </div>
            </div>
            <NavLink
              to="/nayax/cockpit"
              className="text-xs font-medium uppercase tracking-wider bg-white/25 hover:bg-white/40 px-3 py-1.5 rounded shrink-0 whitespace-nowrap"
              data-testid="moma-refills-banner-btn"
            >
              Ouvrir le Cockpit
            </NavLink>
          </div>
        )}
        <header className="bg-white border-b border-[#E5E5E0] no-print">
          <div className="px-8 py-5 flex items-center justify-between">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#575A57]">Module</div>
              <h1 className="font-heading text-2xl font-medium text-[#1A1C1A] mt-0.5" data-testid="page-title">
                {current.label}
              </h1>
            </div>
            <div className="flex items-center gap-4">
              {/* Phase 101fk.4 (Feb 2026) — Install PWA prompt */}
              <InstallPWAButton />
              <div className="text-right">
                <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#575A57]">Date</div>
                <div className="font-mono text-sm text-[#1A1C1A] mt-0.5">
                  {new Date().toLocaleDateString("fr-FR", { weekday: "long", day: "2-digit", month: "long", year: "numeric" })}
                </div>
              </div>
            </div>
          </div>
        </header>
        <div className="px-8 py-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
